/**
 * Admin catalog-transfer API layer — PIM Wave P5 Task 8. Typed calls to the
 * SHIPPED PIM Wave P5 Task 4 `/admin/catalog/transfer/*` router
 * (`apps/api/garden/features/catalog_schema/transfer_router.py` +
 * `transfer.py`), gated server-side on `marketplace.attribute.manage`. This
 * task adds NO backend routes.
 *
 * Mirrors the Pydantic schemas in `transfer.py` EXACTLY:
 * `TransferBundle` (+ its nested `GroupBundle`/`FieldBundle`/`OptionBundle`/
 * `CategoryBundle`/`CategoryAliasBundle`/`BindingBundle`/`NeedMappingBundle`)
 * and `TransferDiff` (+ `DiffBucket`/`DiffEntry`/`DiffTotals`). Decimal
 * columns (`matching_weight`, `weight`, `matching_weight_override`) are
 * serialised as JSON **strings** on the wire (mirrors `matching_weight` in
 * `attribute-fields/api.ts`) — kept as strings to the API boundary.
 *
 * An uploaded bundle file is parsed client-side into a bare `unknown` — this
 * module does NOT re-validate its shape (the server's `extra="forbid"`
 * Pydantic models are the single source of truth for what a valid bundle
 * looks like; a malformed upload simply 422s and the caller surfaces the
 * server's error).
 */
import { apiClient, ApiError } from "@/lib/api-client";

// ---------------------------------------------------------------------
// Bundle DTOs (export shape == import input shape — round-trips verbatim).
// ---------------------------------------------------------------------

/** Mirrors `OptionBundle`. */
export interface TransferOptionBundle {
  value_slug: string;
  label_ru: string;
  label_ky: string | null;
  label_en: string | null;
  sort_order: number;
  is_archived: boolean;
}

/** Mirrors `FieldBundle`. */
export interface TransferFieldBundle {
  slug: string;
  field_type: string;
  name_ru: string;
  name_ky: string | null;
  name_en: string | null;
  hint_ru: string | null;
  hint_ky: string | null;
  hint_en: string | null;
  example_ru: string | null;
  example_ky: string | null;
  example_en: string | null;
  group_slug: string | null;
  unit_kind: string | null;
  canonical_unit: string | null;
  /** `Decimal` on the server, serialised as a JSON string on the wire. */
  matching_weight: string;
  options: TransferOptionBundle[];
}

/** Mirrors `GroupBundle`. */
export interface TransferGroupBundle {
  slug: string;
  name_ru: string;
  name_ky: string | null;
  name_en: string | null;
  sort_order: number;
}

/** Mirrors `CategoryAliasBundle`. */
export interface TransferCategoryAliasBundle {
  alias: string;
  lang: string | null;
}

/** Mirrors `CategoryBundle`. */
export interface TransferCategoryBundle {
  slug: string;
  name_ru: string;
  name_ky: string | null;
  name_en: string | null;
  parent_slug: string | null;
  sort_order: number;
  is_archived: boolean;
  photo_rules: Record<string, unknown> | null;
  aliases: TransferCategoryAliasBundle[];
}

/** Mirrors `BindingBundle`. */
export interface TransferBindingBundle {
  category_slug: string;
  field_slug: string;
  requirement: string;
  sort_order: number;
  visibility_rule: Record<string, unknown> | null;
  matching_weight_override: string | null;
}

/** Mirrors `NeedMappingBundle`. */
export interface TransferNeedMappingBundle {
  slug: string;
  phrases: Record<string, unknown>;
  field_id_slug: string;
  matcher: Record<string, unknown>;
  weight: string;
}

/** Mirrors `TransferBundle` (`version` is always `1` — `BUNDLE_VERSION`). */
export interface TransferBundle {
  version: 1;
  groups: TransferGroupBundle[];
  fields: TransferFieldBundle[];
  categories: TransferCategoryBundle[];
  bindings: TransferBindingBundle[];
  need_mappings: TransferNeedMappingBundle[];
}

// ---------------------------------------------------------------------
// Diff DTOs — mirrors `TransferDiff` / `DiffBucket` / `DiffEntry` / `DiffTotals`.
// ---------------------------------------------------------------------

/** Mirrors `DiffEntry` — `slug` is a compound `"a:b"` key for the entity
 * kinds whose own uniqueness is scoped (option, binding, alias). */
export interface TransferDiffEntry {
  slug: string;
  reason: string;
}

/** Mirrors `DiffBucket`. */
export interface TransferDiffBucket {
  create: TransferDiffEntry[];
  update: TransferDiffEntry[];
  skip: TransferDiffEntry[];
  conflict: TransferDiffEntry[];
}

/** Mirrors `DiffTotals`. */
export interface TransferDiffTotals {
  create: number;
  update: number;
  skip: number;
  conflict: number;
}

/** The 7 entity-kind keys `TransferDiff` carries (excluding `totals`). */
export const TRANSFER_DIFF_KINDS = [
  "groups",
  "fields",
  "options",
  "categories",
  "bindings",
  "aliases",
  "need_mappings",
] as const;
export type TransferDiffKind = (typeof TRANSFER_DIFF_KINDS)[number];

/** Mirrors `TransferDiff`. */
export type TransferDiff = Record<TransferDiffKind, TransferDiffBucket> & {
  totals: TransferDiffTotals;
};

// ---------------------------------------------------------------------
// Requests.
// ---------------------------------------------------------------------

/** `GET /admin/catalog/transfer/export` — the whole live catalog schema. */
export function exportTransferBundle(): Promise<TransferBundle> {
  return apiClient.get<TransferBundle>("/admin/catalog/transfer/export");
}

/**
 * `POST /admin/catalog/transfer/import/dry-run` — a read-only diff. `bundle`
 * is `unknown` (a hand-uploaded file, unvalidated client-side; the server's
 * `extra="forbid"` models are the single source of truth). Unlike
 * `/import/apply`, the dry-run route takes the bundle as the SINGLE
 * top-level body param (`dry_run_import(bundle: TransferBundle, ...)` in
 * `transfer_router.py` — no `Body(embed=True)`), so the bundle must be sent
 * directly, NOT wrapped in `{ bundle }` (that 422s against the
 * `extra="forbid"` `TransferBundle` model).
 */
export function dryRunTransferImport(bundle: unknown): Promise<TransferDiff> {
  return apiClient.post<TransferDiff, unknown>(
    "/admin/catalog/transfer/import/dry-run",
    bundle,
  );
}

/** Mirrors `ImportApplyResponse` — 201, creates a DRAFT changeset. */
export interface ImportApplyResponse {
  changeset_id: string;
  diff: TransferDiff;
}

/**
 * `POST /admin/catalog/transfer/import/apply` — refuses (422) if the diff
 * carries any conflict. `title` omitted (`undefined`) drops out of the JSON
 * body entirely, matching the server's `title: str | None = None` default.
 */
export function applyTransferImport(
  bundle: unknown,
  title?: string,
): Promise<ImportApplyResponse> {
  return apiClient.post<
    ImportApplyResponse,
    { bundle: unknown; title?: string }
  >("/admin/catalog/transfer/import/apply", { bundle, title });
}

// ---------------------------------------------------------------------
// Error-shape helpers — mirrors `changesets/api.ts`'s `ApiError.problem`
// cast idiom (this repo's precedent for reading structured extra fields off
// a problem-details body).
// ---------------------------------------------------------------------

/** The 404 code the transfer-apply endpoint would surface if it ever routed
 * through the `pim.changesets` flag gate (defensive — see the component
 * header comment for why this is currently unreachable in practice). */
export const TRANSFER_CHANGESETS_DISABLED_CODE = "pim.changeset.disabled";

export function isTransferChangesetsDisabledError(error: unknown): boolean {
  return error instanceof ApiError && error.code === TRANSFER_CHANGESETS_DISABLED_CODE;
}

/**
 * Extracts the `diff` off a 422 `ImportConflictResponse` body
 * (`{detail, diff}`) — returns `null` if `error` isn't that shape (the
 * caller falls back to a generic toast).
 */
export function parseImportConflictDiff(error: unknown): TransferDiff | null {
  if (!(error instanceof ApiError)) return null;
  const raw = (error.problem as Record<string, unknown>)["diff"];
  return raw && typeof raw === "object" ? (raw as TransferDiff) : null;
}
